import React, { useMemo } from "react";
import { Button, Dropdown, Space, Tabs, Tag } from "antd";
import { EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { getAgentAircraftReg, getAgentRoundLabel, getAgentStatusLabel, getAgentStatusTone, getAgentStrategyLabel, getAgentTrackStatus } from "../../../lib/agentTrackUtils";

function getTrackActions(item) {
    if (Array.isArray(item?.trackActions)) {
        return item.trackActions.filter(Boolean);
    }
    return item?.trackAction ? [item.trackAction] : [];
}

function stopTabActionEvent(event) {
    event.preventDefault();
    event.stopPropagation();
}

function buildTrackActionMenuLabel(action) {
    return (
        <span className={`schedule-draft-timetable-track-action-menu-label ${action.disabled ? "schedule-draft-timetable-track-action-menu-label-disabled" : ""}`}>
            <span>{action.label}</span>
            {action.disabledReason ? <span className="schedule-draft-timetable-track-action-menu-reason">{action.disabledReason}</span> : null}
        </span>
    );
}

function TimetableTabActionMenu({ title, track, actions = [], onTrackAction, children }) {
    const menu = useMemo(() => ({
        items: actions.map((action, index) => ({
            key: `${action.type || "track-action"}:${action.trackId || track?.id || index}`,
            danger: Boolean(action.danger && !action.disabled),
            label: buildTrackActionMenuLabel(action),
        })),
        onClick: ({ key, domEvent }) => {
            domEvent?.preventDefault?.();
            domEvent?.stopPropagation?.();
            const action = actions.find((candidate, index) => `${candidate.type || "track-action"}:${candidate.trackId || track?.id || index}` === key);
            if (!action) {
                return;
            }
            onTrackAction?.(action, action.track || track);
        },
    }), [actions, onTrackAction, track]);

    if (!actions.length) {
        return children;
    }

    const actionButton = (
        <Dropdown menu={menu} trigger={["click"]} placement="bottomRight">
            <Button
                type="text"
                size="small"
                className="schedule-draft-timetable-tab-action-button"
                icon={<MoreOutlined />}
                aria-label={`打开${title}轨操作`}
                onClick={stopTabActionEvent}
                onMouseDown={stopTabActionEvent}
            />
        </Dropdown>
    );

    return (
        <Dropdown menu={menu} trigger={["contextMenu"]} placement="bottomRight">
            <span className="schedule-draft-timetable-tab-label" onContextMenu={(event) => event.stopPropagation()}>
                {children}
                {actionButton}
            </span>
        </Dropdown>
    );
}

function buildTimetableTabLabel({ title, editable, track, actions, onTrackAction }) {
    const content = (
        <Space size={6} className={editable ? "schedule-draft-timetable-tab-editable" : "schedule-draft-timetable-tab-readonly"}>
            {editable ? <EditOutlined /> : <EyeOutlined />}
            <span>{title}</span>
            <Tag color={editable ? "blue" : "default"}>{editable ? "可编辑" : "只读"}</Tag>
        </Space>
    );

    return (
        <TimetableTabActionMenu title={title} track={track} actions={actions} onTrackAction={onTrackAction}>
            {content}
        </TimetableTabActionMenu>
    );
}

function getReferenceTabTitle(item) {
    if (item.sourceType === "AGENT" && item.track) {
        return item.agentRoundLabel || getAgentRoundLabel(item.track);
    }

    if (item.sourceType === "HISTORY" && item.track) {
        return item.track.name || "历史快照";
    }

    return item.label || item.key;
}

function buildAgentPlanTabLabel(item, onTrackAction) {
    const track = item.track;
    const status = getAgentTrackStatus(track);
    const title = getAgentAircraftReg(track) || "Agent";
    const content = (
        <Space size={6} className="schedule-draft-timetable-tab-agent-plan">
            <span>{title}</span>
            <Tag color="purple">{getAgentStrategyLabel(track)}</Tag>
            <Tag color={getAgentStatusTone(status)}>{getAgentStatusLabel(status)}</Tag>
        </Space>
    );

    return (
        <TimetableTabActionMenu title={title} track={track} actions={getTrackActions(item)} onTrackAction={onTrackAction}>
            {content}
        </TimetableTabActionMenu>
    );
}

function buildTimetableTabGroups(referenceTrackItems, sourceTrackOptions, onTrackAction) {
    const editableSourceOptions = (sourceTrackOptions || []).filter((option) => option.kind === "draft" || option.kind === "editing");
    const primaryItems = editableSourceOptions.length
        ? editableSourceOptions.map((option) => ({
              key: option.value,
              label: buildTimetableTabLabel({ title: option.label || "当前计划", editable: true, track: option.track, actions: getTrackActions(option), onTrackAction }),
          }))
        : [
              {
                  key: "draft-current",
                  label: buildTimetableTabLabel({ title: "当前计划", editable: true }),
              },
          ];
    const agentGroupsByKey = new Map();

    referenceTrackItems.forEach((item) => {
        if (item.sourceType !== "AGENT" || !item.track) {
            primaryItems.push({
                key: item.key,
                label: buildTimetableTabLabel({ title: getReferenceTabTitle(item), editable: false, track: item.track, actions: getTrackActions(item), onTrackAction }),
            });
            return;
        }

        const context = item.track.agent_context || {};
        const groupKey = context.agent_round_key || `agent-round:${context.agent_round || context.ag_request_id || context.ag_run_id || "unassigned"}`;
        if (!agentGroupsByKey.has(groupKey)) {
            agentGroupsByKey.set(groupKey, {
                key: groupKey,
                label: item.agentRoundLabel || getReferenceTabTitle(item),
                items: [],
            });
        }
        agentGroupsByKey.get(groupKey).items.push(item);
    });

    const agentGroups = Array.from(agentGroupsByKey.values());
    agentGroups.forEach((group) => {
        primaryItems.push({
            key: group.key,
            label: (
                <Space size={6} className="schedule-draft-timetable-tab-agent-round">
                    <span>{group.label}</span>
                    <Tag color="purple">AI 轮次</Tag>
                    <Tag>{group.items.length} 个方案</Tag>
                </Space>
            ),
        });
    });

    return { primaryItems, agentGroups };
}

const TimetableTabs = ({ activeKey, referenceTrackItems, sourceTrackOptions = [], onChange, onTrackAction }) => {
    const { primaryItems, agentGroups } = useMemo(() => buildTimetableTabGroups(referenceTrackItems, sourceTrackOptions, onTrackAction), [onTrackAction, referenceTrackItems, sourceTrackOptions]);
    const activeAgentGroup = agentGroups.find((group) => group.items.some((item) => item.key === activeKey));
    const activePrimaryKey = activeAgentGroup?.key || activeKey || "draft-current";
    const agentPlanItems = activeAgentGroup?.items.map((item) => ({
        key: item.key,
        label: buildAgentPlanTabLabel(item, onTrackAction),
    })) || [];

    const handlePrimaryChange = (nextKey) => {
        const nextAgentGroup = agentGroups.find((group) => group.key === nextKey);
        onChange?.(nextAgentGroup ? nextAgentGroup.items[0]?.key : nextKey);
    };

    return (
        <div className="schedule-draft-timetable-tabs">
            <Tabs activeKey={activePrimaryKey} onChange={handlePrimaryChange} items={primaryItems} />
            {activeAgentGroup ? <Tabs className="schedule-draft-timetable-agent-plan-tabs" size="small" activeKey={activeKey} onChange={onChange} items={agentPlanItems} /> : null}
        </div>
    );
};

export default TimetableTabs;
